import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../supabase'
import { 
  Edit, 
  Save, 
  X,
  Calendar,
  Trophy
} from 'lucide-react'

const ProfilePage = () => {
  const { user, profile, updateProfile } = useAuth()
  const [stats, setStats] = useState({
    remindersSubmitted: 0,
    remindersRewritten: 0,
    upvotesReceived: 0
  })
  const [isEditingStatus, setIsEditingStatus] = useState(false)
  const [newStatus, setNewStatus] = useState(profile?.status || '')
  const [loading, setLoading] = useState(true)

  const achievements = {
    '10_rewrites': { 
      title: '10 Reminders Rewritten', 
      emoji: '📝', 
      description: 'You\'ve helped rewrite 10 reminders!' 
    },
    'first_received': { 
      title: 'First Rewrite Received', 
      emoji: '💌', 
      description: 'Someone rewrote your reminder for the first time!' 
    },
    'popular_rewriter': { 
      title: 'Popular Rewriter', 
      emoji: '🏆', 
      description: 'One of your rewrites got 10+ votes!' 
    },
    '5_days_streak': { 
      title: '5 Days in a Row', 
      emoji: '🔥', 
      description: 'You\'ve rewritten reminders for 5 consecutive days!' 
    },
    'wholesome_reminder': { 
      title: 'Most Wholesome Reminder', 
      emoji: '🧸', 
      description: 'Your reminder was voted most wholesome!' 
    }
  }

  const loadUserStats = useCallback(async () => {
    try {
      const [reminders, rewrites] = await Promise.all([
        db.getUserReminders(user.id),
        db.getUserRewrites(user.id)
      ])
      const totalVotes = rewrites.reduce((sum, rewrite) => sum + (rewrite.votes || 0), 0)
      setStats({
        remindersSubmitted: reminders.length,
        remindersRewritten: rewrites.length,
        upvotesReceived: totalVotes
      })
    } catch (error) {
      console.error('Error loading stats:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (user) {
      loadUserStats()
    }
  }, [user, loadUserStats])

  const handleSaveStatus = async () => {
    try {
      await updateProfile({ status: newStatus })
      setIsEditingStatus(false)
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Failed to update status. Please try again.')
    }
  }

  const handleCancelEdit = () => {
    setNewStatus(profile?.status || '')
    setIsEditingStatus(false)
  }

  // Friends list (mocked for now)
  const friends = profile?.friends || [];

  // Mocked daily streak
  const dailyStreak = 4; // Replace with real logic later

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
      {/* Profile Header */}
      <div className="card mb-8">
        <div className="flex items-start space-x-6">
          {profile?.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt={profile.name}
              className="w-24 h-24 rounded-full border-4 border-primary-100"
            />
          ) : (
            <div className="w-24 h-24 bg-primary-100 rounded-full border-4 border-primary-200 flex items-center justify-center">
              <span className="text-3xl text-primary-600 font-bold">
                {profile?.name?.charAt(0) || '?'}
              </span>
            </div>
          )}
          
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {profile?.name || 'Anonymous'}
            </h1>
            <p className="text-gray-600 mb-4">
              {profile?.email}
            </p>
            
            {/* Status */}
            <div className="flex items-center space-x-2">
              {isEditingStatus ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    placeholder="What's your current vibe?"
                    className="input-field flex-1"
                    maxLength={100}
                  />
                  <button
                    onClick={handleSaveStatus}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                  >
                    <Save size={16} />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span className="text-gray-700">
                    My current vibe: {profile?.status || 'Just getting started!'}
                  </span>
                  <button
                    onClick={() => setIsEditingStatus(true)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <Edit size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card text-center">
          <div className="text-3xl mb-2">📝</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            Reminders Submitted
          </h3>
          <p className="text-3xl font-bold text-primary-600">
            {stats.remindersSubmitted}
          </p>
        </div>
        
        <div className="card text-center">
          <div className="text-3xl mb-2">✍️</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            Reminders Rewritten
          </h3>
          <p className="text-3xl font-bold text-primary-600">
            {stats.remindersRewritten}
          </p>
        </div>
        
        <div className="card text-center">
          <div className="text-3xl mb-2">❤️</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-1">
            Upvotes Received
          </h3>
          <p className="text-3xl font-bold text-primary-600">
            {stats.upvotesReceived}
          </p>
        </div>
      </div>

      {/* Achievements */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
          <Trophy className="mr-2" size={20} />
          Trophy Case
        </h2>

        {profile?.achievements && profile.achievements.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profile.achievements.map((achievement, index) => (
              <div
                key={index}
                className="flex items-center space-x-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200"
              >
                <div className="text-3xl">
                  {achievements[achievement]?.emoji || '🏆'}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">
                    {achievements[achievement]?.title || achievement}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {achievements[achievement]?.description || 'Achievement unlocked!'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">🏆</div>
            <p className="text-gray-600 mb-2">No achievements yet!</p>
            <p className="text-sm text-gray-500">
              Start rewriting reminders to earn your first trophy.
            </p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="card mt-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
          <Calendar className="mr-2" size={20} />
          Recent Activity
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl">🎯</div>
            <div>
              <p className="font-medium text-gray-800">Joined Strangeminder</p>
              <p className="text-sm text-gray-500">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'Recently'}
              </p>
            </div>
          </div>
          
          {stats.remindersSubmitted > 0 && (
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <div className="text-2xl">📝</div>
              <div>
                <p className="font-medium text-gray-800">Created your first reminder</p>
                <p className="text-sm text-gray-500">You're helping others find meaning!</p>
              </div>
            </div>
          )}
          
          {stats.remindersRewritten > 0 && (
            <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl">✍️</div>
              <div>
                <p className="font-medium text-gray-800">Started rewriting reminders</p>
                <p className="text-sm text-gray-500">You're making the world more meaningful!</p>
              </div>
            </div>
          )}
          
          {stats.upvotesReceived > 0 && (
            <div className="flex items-center space-x-3 p-3 bg-pink-50 rounded-lg">
              <div className="text-2xl">❤️</div>
              <div>
                <p className="font-medium text-gray-800">Received your first upvote</p>
                <p className="text-sm text-gray-500">People love your creative rewrites!</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Daily Streak */}
      <div className="card mb-8 flex items-center gap-4">
        <span className="text-3xl">🔥</span>
        <div>
          <div className="font-semibold">{dailyStreak} days in a row!</div>
          {dailyStreak >= 3 && (
            <div className="text-primary-600 font-bold animate-bounce">Streak! 🎉</div>
          )}
        </div>
      </div>

      {/* Friends List */}
      <div className="card mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">👯 Friends</h2>
        {friends.length === 0 ? (
          <p className="text-gray-500">No friends yet. Add some from the community!</p>
        ) : (
          <ul className="flex flex-wrap gap-4">
            {friends.map(friend => (
              <li key={friend.id} className="flex flex-col items-center">
                <img src={friend.avatar_url} alt={friend.name} className="w-12 h-12 rounded-full border-2 border-primary-200 mb-1" />
                <span className="text-xs font-medium mb-1">{friend.name}</span>
                <button 
                  className="text-xs text-red-500 hover:underline mb-1"
                  onClick={() => alert('Remove friend feature coming soon!')}
                >
                  Remove
                </button>
                <button 
                  className="text-xs text-primary-600 hover:underline"
                  onClick={() => alert('View trophies feature coming soon!')}
                >
                  View Trophies
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default ProfilePage 